import { HttpErrorResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface HttpErrorLogEntry {
  readonly timestamp: string;
  readonly request: {
    readonly method: string;
    readonly url: string;
    readonly headers: Record<string, string[]>;
    readonly payload: unknown;
  };
  readonly response: {
    readonly status: number;
    readonly statusText: string;
    readonly url: string | null;
    readonly headers: Record<string, string[]>;
    readonly body: unknown;
  };
  readonly browserError: unknown;
}

export interface HttpErrorToastGroup {
  readonly key: string;
  readonly method: string;
  readonly endpoint: string;
  readonly attempts: readonly HttpErrorLogEntry[];
}

@Injectable({ providedIn: 'root' })
export class HttpErrorToastService {
  private readonly groupsSubject = new BehaviorSubject<readonly HttpErrorToastGroup[]>([]);

  readonly groups$ = this.groupsSubject.asObservable();

  report(request: HttpRequest<unknown>, error: HttpErrorResponse): void {
    const endpoint = this.getEndpoint(request.urlWithParams);
    const key = `${request.method.toUpperCase()} ${endpoint}`;
    const entry = this.createLogEntry(request, error);
    const groups = [...this.groupsSubject.value];
    const existingIndex = groups.findIndex((group) => group.key === key);

    if (existingIndex === -1) {
      groups.push({ key, method: request.method.toUpperCase(), endpoint, attempts: [entry] });
    } else {
      const existing = groups[existingIndex];
      groups[existingIndex] = { ...existing, attempts: [...existing.attempts, entry] };
    }

    this.groupsSubject.next(groups);
  }

  dismiss(key: string): void {
    this.groupsSubject.next(this.groupsSubject.value.filter((group) => group.key !== key));
  }

  latestLog(group: HttpErrorToastGroup): string {
    return JSON.stringify(this.withGroupMetadata(group, [group.attempts[group.attempts.length - 1]]), null, 2);
  }

  allVisibleLogs(groups: readonly HttpErrorToastGroup[]): string {
    return JSON.stringify({
      visibleGroupCount: groups.length,
      groups: groups.map((group) => this.withGroupMetadata(group, group.attempts)),
    }, null, 2);
  }

  private withGroupMetadata(group: HttpErrorToastGroup, attempts: readonly HttpErrorLogEntry[]): unknown {
    return {
      group: {
        method: group.method,
        endpoint: group.endpoint,
        attemptCount: group.attempts.length,
        copiedAttemptCount: attempts.length,
      },
      attempts,
    };
  }

  private createLogEntry(request: HttpRequest<unknown>, error: HttpErrorResponse): HttpErrorLogEntry {
    return {
      timestamp: new Date().toISOString(),
      request: {
        method: request.method,
        url: request.urlWithParams,
        headers: this.headersToRecord(request.headers),
        payload: this.toSerializable(request.body),
      },
      response: {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        headers: this.headersToRecord(error.headers),
        body: this.toSerializable(error.error),
      },
      browserError: this.toSerializable(error.error instanceof Error ? error.error : null),
    };
  }

  private getEndpoint(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url.split('?')[0] || url;
    }
  }

  private headersToRecord(headers: HttpHeaders): Record<string, string[]> {
    return headers.keys().reduce<Record<string, string[]>>((result, key) => {
      result[key] = headers.getAll(key) ?? [];
      return result;
    }, {});
  }

  private toSerializable(value: unknown, seen = new WeakSet<object>()): unknown {
    if (value === null || value === undefined || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value ?? null;
    }

    if (typeof value === 'bigint') return `${value}n`;
    if (typeof value === 'function' || typeof value === 'symbol') return String(value);
    if (value instanceof Date) return value.toISOString();
    if (value instanceof Error) {
      return { name: value.name, message: value.message, stack: value.stack ?? null };
    }
    if (typeof File !== 'undefined' && value instanceof File) {
      return { type: 'File', name: value.name, size: value.size, mimeType: value.type, lastModified: value.lastModified };
    }
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      return { type: 'Blob', size: value.size, mimeType: value.type };
    }
    if (typeof FormData !== 'undefined' && value instanceof FormData) {
      const entries: Record<string, unknown[]> = {};
      value.forEach((item, key) => {
        (entries[key] ??= []).push(this.toSerializable(item, seen));
      });
      return { type: 'FormData', entries };
    }
    if (typeof value !== 'object') return String(value);
    if (seen.has(value)) return '[Circular]';
    seen.add(value);

    if (Array.isArray(value)) return value.map((item) => this.toSerializable(item, seen));

    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((result, [key, item]) => {
      result[key] = this.toSerializable(item, seen);
      return result;
    }, {});
  }
}
